import React from 'react';
import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { basicSort } from '../../utils/misc';
import { Loader } from '../feedback';

interface GridTemplateOptions {
  includeExpandColumn?: boolean;
  expandColumnWidth?: string;
  defaultColumnWidth?: string;
}

function getGridTemplate(columns: any[] = [], options: GridTemplateOptions = {}) {
  const {
    includeExpandColumn = true,
    expandColumnWidth = '40px',
    defaultColumnWidth = '1fr',
  } = options;

  const columnWidths = columns.map(() => defaultColumnWidth);

  if (includeExpandColumn) {
    columnWidths.push(expandColumnWidth);
  }

  return columnWidths.join(' ');
}

/**
 * ExpandableTable
 *
 * A reusable, sortable, expandable table component built with MUI and CSS Grid.
 * Designed for internal dashboards where rows may reveal additional details.
 *
 * Features:
 * - Column-based sorting (opt-in per column)
 * - Single-row expansion with animated collapse
 * - Optional header background styling
 * - Loading and empty states
 * - Column-level alignment control
 *
 * Responsibilities:
 * - Layout and interaction only
 * - No domain or business logic
 *
 * Props:
 *
 * @param {Array<Object>} columns
 *  Column configuration describing how each column is rendered.
 *  Each column supports:
 *    - id: string (required)
 *    - label: string (required)
 *    - dataKey?: string (row field used for display and sorting)
 *    - sortable?: boolean
 *    - align?: 'left' | 'center' | 'right'
 *    - renderCell?: (value, row) => ReactNode
 *
 * @param {Array<Object>} data
 *  Array of row objects to render.
 *
 * @param {(row: Object) => ReactNode} renderExpandedRow
 *  Function that renders expanded content for a row.
 *  If not provided, expansion is visually disabled.
 *
 * @param {boolean} loading
 *  Whether the table is in a loading state.
 *
 * @param {string | ReactNode} emptyMessage
 *  Message displayed when there is no data.
 *
 * @param {string} defaultSortKey
 *  Column dataKey to apply initial sorting.
 *
 * @param {*} resetSortKey
 *  External trigger to reset sorting and expansion state.
 *
 * @param {boolean} hasHeaderBg
 *  Whether the header row should render with a background color.
 *
 * Behavior notes:
 * - Sorting is disabled when data length <= 1
 * - Sorting collapses any expanded row
 * - Only one row can be expanded at a time
 * - Expanded content does not participate in sorting
 */

interface ExpandableTableProps {
  columns: any[];
  data?: any[];
  renderExpandedRow?: (row: any) => React.ReactNode;
  emptyMessage?: string;
  loading?: boolean;
  defaultSortKey?: string;
  resetSortKey?: any;
  headerBg?: boolean;
  onExpandedRowChange?: (row: any) => void;
  expandColumnWidth?: string;
  hideHeader?: boolean;
}

export default function ExpandableTable({
  columns,
  data = [],
  renderExpandedRow,
  emptyMessage = 'No data available',
  loading = false,
  defaultSortKey,
  resetSortKey,
  headerBg = false,
  onExpandedRowChange,
  expandColumnWidth = '40px',
  hideHeader = false,
}: ExpandableTableProps) {
  const gridTemplateColumns = getGridTemplate(columns, { expandColumnWidth });

  const [expandedRowId, setExpandedRowId] = useState(null);
  const [sortKey, setSortKey] = useState(defaultSortKey || null);
  const [sortOrder, setSortOrder] = useState('asc');

  const canSort = Array.isArray(data) && data.length > 1;

  const sortedData = useMemo(() => {
    if (!Array.isArray(data) || !sortKey) return data;
    return basicSort([...data], sortKey!, sortOrder as 'asc' | 'desc');
  }, [data, sortKey, sortOrder]);

  const handleSort = (col) => {
    if (!col.sortable || !canSort || !col.dataKey) return;

    if (sortKey === col.dataKey) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.dataKey);
      setSortOrder('asc');
    }

    setExpandedRowId(null); // collapse on sort
  };

  useEffect(() => {
    if (resetSortKey !== undefined) {
      setSortKey(defaultSortKey || null);
      setSortOrder('asc');
      setExpandedRowId(null); // important UX detail
    }
  }, [resetSortKey, defaultSortKey]);

  return (
    <div className='column gap-3'>
      {!hideHeader && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns,
            padding: '12px 16px',
            fontWeight: 500,
            bgcolor: headerBg ? 'header.main' : 'background.default',
          }}
        >
          {columns.map((col) => (
            <Box
              role={col.sortable ? 'button' : undefined}
              aria-sort={
                sortKey === col.dataKey
                  ? sortOrder === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none'
              }
              key={col.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                justifyContent:
                  col.align === 'right'
                    ? 'flex-end'
                    : col.align === 'center'
                      ? 'center'
                      : 'flex-start',
                cursor: col.sortable && canSort ? 'pointer' : 'default',
                userSelect: 'none',
                textAlign: col.align || 'left',
              }}
              onClick={col.sortable && canSort ? () => handleSort(col) : undefined}
            >
              <span>{col.label}</span>

              {col.sortable && (
                <Icon
                  width={24}
                  icon='ri:arrow-drop-down-line'
                  style={{
                    opacity: canSort ? 1 : 0.6,
                    transform:
                      sortKey === col.dataKey
                        ? sortOrder === 'asc'
                          ? 'rotate(0deg)'
                          : 'rotate(180deg)'
                        : 'rotate(0deg)',
                    transition: 'transform 150ms ease, opacity 150ms ease',
                  }}
                />
              )}
            </Box>
          ))}
          <Box />
        </Box>
      )}

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'grid', alignItems: 'center', padding: '24px' }}>
          <Loader />
        </Box>
      )}

      {!loading && data.length === 0 && (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'grey.300',
            borderRadius: 2,
            padding: '16px',
            textAlign: 'center',
          }}
        >
          <Typography variant='body2' fontStyle='italic' color='text.secondary'>
            {emptyMessage}
          </Typography>
        </Box>
      )}

      {/* Rows */}
      {!loading &&
        sortedData.map((row) => (
          <ExpandableTableRow
            key={row.id}
            row={row}
            columns={columns}
            gridTemplateColumns={gridTemplateColumns}
            renderExpandedRow={renderExpandedRow}
            expanded={expandedRowId === row.id}
            onToggle={() =>
              setExpandedRowId((prev) => {
                const next = prev === row.id ? null : row.id;
                onExpandedRowChange?.(next);
                return next;
              })
            }
          />
        ))}
    </div>
  );
}

function ExpandableTableRow({
  row,
  columns,
  gridTemplateColumns,
  expanded,
  onToggle,
  renderExpandedRow,
}) {
  const canExpand = Boolean(renderExpandedRow);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Main row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          alignItems: 'center',
          padding: '12px 16px',
          cursor: 'pointer',
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (canExpand) onToggle?.();
        }}
      >
        {columns.map((col) => {
          const value = col.dataKey ? row[col.dataKey] : null;
          return <Box key={col.id}>{col.renderCell ? col.renderCell(value, row) : value}</Box>;
        })}

        <IconButton
          size='small'
          disableRipple
          aria-expanded={expanded}
          sx={{
            border: '1px solid',
            borderColor: 'grey.500',
            borderRadius: '50%',
            padding: '2px',
            width: 24,
            height: 24,
            justifySelf: 'end',
            ':hover': { bgcolor: 'grey.50' },

            '&.Mui-disabled': { borderColor: 'grey.300' },
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (canExpand) onToggle?.();
          }}
        >
          <Icon
            icon='ri:arrow-drop-down-line'
            style={{
              opacity: canExpand ? 1 : 0.3,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease, opacity 150ms ease',
            }}
          />
        </IconButton>
      </Box>

      {/* Expanded row */}
      <Collapse in={expanded} timeout='auto' unmountOnExit>
        <Box
          sx={{
            padding: '12px 16px',
            bgcolor: 'background.default',
          }}
        >
          {renderExpandedRow(row)}
        </Box>
      </Collapse>
    </Box>
  );
}
